'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Tab, Tabs, Box, Typography } from '@mui/material';
import { EventCard } from '@scorenco/components';
import { RouteLink, useTranslation } from '@scorenco/core';
import { differenceInSeconds } from 'date-fns';
import { useRouter } from 'next/router';

import { TabPanel } from '@/components';
import { T_KEYS } from '@/translations';

import { GetPreviousAndNextEventsDocument } from './_internal/Widget.query.generated';
import type { GetPreviousAndNextEventsQuery } from './_internal/Widget.query.generated';

interface TimeUnitProps {
  value: number;
  label: string;
}

const TimeUnit = ({ value, label }: TimeUnitProps) => (
  <Box
    sx={{
      textAlign: 'center',
      minWidth: { xs: '35px', sm: '45px', md: '60px' },
      p: { xs: 0.3, sm: 0.5, md: 1 },
      borderRadius: 1,
    }}
  >
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
      }}
    >
      {String(value).padStart(2, '0')}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        opacity: 0.8,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontSize: { xs: '0.5rem', sm: '0.6rem', md: '0.7rem' },
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface CountdownTimerProps {
  targetDate?: string | null;
}

const CountdownTimer = ({ targetDate }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!targetDate || new Date(targetDate) < new Date()) return undefined;

    const calculateTimeLeft = () => {
      const totalSeconds = differenceInSeconds(
        new Date(targetDate),
        new Date()
      );
      if (totalSeconds <= 0) return;

      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!targetDate || new Date(targetDate) < new Date()) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 0.5, sm: 1, md: 2 },
        justifyContent: 'center',
        mt: { xs: 1, sm: 1.5, md: 2 },
        p: { xs: 0.5, sm: 1, md: 2 },
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 2,
        backdropFilter: 'blur(8px)',
      }}
    >
      <TimeUnit value={timeLeft.days} label="jours" />
      <TimeUnit value={timeLeft.hours} label="heures" />
      <TimeUnit value={timeLeft.minutes} label="min" />
      <TimeUnit value={timeLeft.seconds} label="sec" />
    </Box>
  );
};

type TeamRef = {
  club_id?: number;
  is_team_of_club?: number;
  logo?: string;
  name_in_competition?: string;
};

type QueryEvent = NonNullable<
  NonNullable<GetPreviousAndNextEventsQuery['previousEvent']>[number]
>;

const getStringFromUnknown = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const getNumberFromUnknown = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const getEventTeams = (value: unknown): TeamRef[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item !== null
    )
    .map((team) => ({
      club_id: getNumberFromUnknown(team.club_id),
      is_team_of_club: getNumberFromUnknown(team.is_team_of_club),
      logo: getStringFromUnknown(team.logo),
      name_in_competition: getStringFromUnknown(team.name_in_competition),
    }));
};

const getEventTargetDate = (event?: QueryEvent) =>
  getStringFromUnknown(event?.time) ?? getStringFromUnknown(event?.date);

const isBeforeMatchStart = (event?: QueryEvent) => {
  const targetDate = getEventTargetDate(event);
  return targetDate ? new Date() < new Date(targetDate) : false;
};

const getDisplayEvent = (event?: QueryEvent): QueryEvent | undefined => {
  if (!event || !isBeforeMatchStart(event)) {
    return event;
  }

  return { ...event, status: 'to_come' };
};

interface EventSectionProps {
  event?: QueryEvent | null;
  title: keyof typeof T_KEYS;
  showShortName?: boolean;
  clubId?: number;
  t: (key: string) => string;
}

const EventSection = ({
  event,
  title,
  showShortName,
  clubId,
  t,
}: EventSectionProps) => {
  const theme = useTheme();
  if (!event) {
    return (
      <Typography
        variant="h5"
        className="no-event-message"
        sx={{
          textAlign: 'center',
          padding: '0.5rem',
          mt: '1rem',
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
        }}
      >
        {title === 'NEXT_EVENT'
          ? 'Aucun match à venir'
          : 'Aucun match précédent'}{' '}
        n'a été trouvé pour cette équipe
      </Typography>
    );
  }

  return (
    <>
      <RouteLink
        href={
          event.url ? process.env.NEXT_PUBLIC_WEB_URL + event.url : undefined
        }
      >
        <a target="_blank">
          <h1
            css={css`
              font-size: 1rem;
              margin: 1.5rem auto 0;
              padding: 0.8rem;
              width: 50%;
              text-align: center;
              background-color: ${theme.vars.palette.primary.main};
              color: ${theme.vars.palette.background.paper};
              border-radius: 8px;

              ${theme.breakpoints.down('md')} {
                font-size: 0.8rem;
                margin: 1rem auto 0;
                padding: 0.5rem;
                width: 90%;
              }
            `}
          >
            {t(T_KEYS[title])}
          </h1>
        </a>
      </RouteLink>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <RouteLink
          href={
            event.url ? process.env.NEXT_PUBLIC_WEB_URL + event.url : undefined
          }
        >
          <a target="_blank">
            <EventCard
              event={getDisplayEvent(event)}
              className="event-card"
              showShortName={showShortName}
              clubId={clubId}
            />
          </a>
        </RouteLink>
        {title === 'NEXT_EVENT' && (
          <CountdownTimer targetDate={getEventTargetDate(event)} />
        )}
      </Box>
    </>
  );
};

type PreviousNextWidgetProps = {
  teams?: {
    team_id?: number;
    club_id?: number;
    id?: number;
    name_in_club?: string;
    name_in_competition?: string;
  }[];
  settings?: {
    show_name_in_club?: boolean;
    display_priority?: string;
  };
};

export const PreviousNextWidget = ({
  teams,
  settings = {
    show_name_in_club: false,
    display_priority: 'results',
  },
}: PreviousNextWidgetProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [currentTeamTab, setCurrentTeamTab] = useState(0);

  const selectedTeam = useMemo(
    () => teams?.[currentTeamTab],
    [teams, currentTeamTab]
  );

  const displayPriority = settings?.display_priority || '';

  const defaultTab = useMemo(() => {
    if (displayPriority === 'results' || displayPriority === '') return 0;
    if (displayPriority === 'calendar') return 1;
    return 0;
  }, [displayPriority]);

  const [userSelectedTab, setUserSelectedTab] = useState<number | null>(null);
  const currentTab = userSelectedTab ?? defaultTab;

  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setUserSelectedTab(newValue);
    },
    []
  );

  const { data: eventsData } = useQuery(GetPreviousAndNextEventsDocument, {
    variables: {
      teamId: Number(selectedTeam?.team_id),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !selectedTeam?.team_id,
    context: {
      role: 'anonymous',
    },
  });

  const previousEvent = eventsData?.previousEvent?.[0];
  const nextEvent = eventsData?.nextEvent?.[0];

  const renderTabs = () => {
    const nextEventTargetDate = getEventTargetDate(nextEvent);
    const nextEventTeams = getEventTeams(nextEvent?.teams);

    if (displayPriority === '') {
      return (
        <TabPanel
          value={currentTab}
          index={0}
          className="tab-panel-previous-next"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: { xs: 0.5, sm: 1, md: 2 },
              gap: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            <Box sx={{ flex: { xs: 1, sm: 2 }, width: '100%' }}>
              <EventSection
                event={previousEvent}
                title="PREVIOUS_EVENT"
                showShortName={settings?.show_name_in_club}
                clubId={selectedTeam?.club_id}
                t={t}
              />
            </Box>
            <RouteLink
              href={
                nextEvent?.url
                  ? process.env.NEXT_PUBLIC_WEB_URL + nextEvent.url
                  : undefined
              }
            >
              <a target="_blank">
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 0.75, md: 1 },
                    minWidth: { xs: '100%', sm: '180px' },
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    p: { xs: 0.5, sm: 1, md: 1.5 },
                    mt: { xs: 1, sm: 0 },
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="img"
                    src={
                      nextEventTeams.find(
                        (team) =>
                          team.club_id !== selectedTeam?.club_id ||
                          team.is_team_of_club === 0
                      )?.logo
                    }
                    alt={
                      nextEventTeams.find(
                        (team) =>
                          team.club_id !== selectedTeam?.club_id ||
                          team.is_team_of_club === 0
                      )?.name_in_competition
                    }
                    sx={{
                      width: { xs: 35, sm: 45, md: 50 },
                      height: { xs: 35, sm: 45, md: 50 },
                      objectFit: 'contain',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                    }}
                  >
                    {
                      nextEventTeams.find(
                        (team) =>
                          team.club_id !== selectedTeam?.club_id ||
                          team.is_team_of_club === 0
                      )?.name_in_competition
                    }
                  </Typography>
                  <CountdownTimer targetDate={nextEventTargetDate} />
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                    }}
                  >
                    {nextEventTeams.findIndex(
                      (team) =>
                        team.club_id === selectedTeam?.club_id ||
                        team.is_team_of_club === 1
                    ) === 0
                      ? 'Domicile - '
                      : 'Extérieur - '}
                    {nextEventTargetDate
                      ? new Date(nextEventTargetDate).toLocaleDateString(
                          'fr-FR',
                          {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )
                      : ''}
                  </Typography>
                </Box>
              </a>
            </RouteLink>
          </Box>
        </TabPanel>
      );
    }

    return (
      <>
        <TabPanel value={currentTab} index={0} className="tab-panel-previous">
          <Box sx={{ paddingTop: 2 }}>
            <EventSection
              event={previousEvent}
              title="PREVIOUS_EVENT"
              showShortName={settings?.show_name_in_club}
              clubId={selectedTeam?.club_id}
              t={t}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1} className="tab-panel-next">
          <Box sx={{ paddingTop: 2 }}>
            <EventSection
              event={nextEvent}
              title="NEXT_EVENT"
              showShortName={settings?.show_name_in_club}
              clubId={selectedTeam?.club_id}
              t={t}
            />
          </Box>
        </TabPanel>
      </>
    );
  };

  return (
    <Box
      className="previous-next-widget"
      sx={{ width: '100%', marginBottom: '1.5rem' }}
    >
      {(teams?.length ?? 0) > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTeamTab}
            onChange={(_, tabIndex) => setCurrentTeamTab(tabIndex)}
            variant={'scrollable'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            css={() => css`
              .MuiTabs-scrollButtons.Mui-disabled {
                opacity: 0.3;
              }
            `}
          >
            {teams?.map((t) => (
              <Tab key={t.team_id} label={t.name_in_club} />
            ))}
          </Tabs>
        </Box>
      )}

      <Box
        className="tabs-container"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          variant="fullWidth"
          className="tabs"
        >
          {(() => {
            const previousEventDateStr = getStringFromUnknown(
              previousEvent?.date
            );
            const previousEventDate = previousEventDateStr
              ? new Date(previousEventDateStr)
              : undefined;

            return displayPriority !== '' ? (
              <Tab
                label={
                  previousEventDate &&
                  previousEventDate.getDate() === new Date().getDate()
                    ? "Aujourd'hui"
                    : t(T_KEYS.PREVIOUS_EVENT)
                }
                className="tab-previous"
              />
            ) : null;
          })()}
          {displayPriority !== '' ? (
            <Tab label={t(T_KEYS.NEXT_EVENT)} className="tab-next" />
          ) : (
            <Tab label={t(T_KEYS.PREVIOUS_NEXT_EVENT)} />
          )}
        </Tabs>
      </Box>

      {renderTabs()}
    </Box>
  );
};
